// 游戏主应用 - 串联所有模块

import type { LevelConfig, PieceData } from './types.js';
import { PuzzleGame } from './core/PuzzleGame.js';
import { ImageLoader, loadLevelConfig, loadLevelImage } from './render/ImageLoader.js';
import { PieceRenderer } from './render/PieceRenderer.js';
import { GameRenderer, GameLayout } from './render/GameRenderer.js';
import { UI } from './ui/UI.js';
import { addCompletedLevel, getCompletedLevels } from './utils/storage.js';
import { cellToPixel, drawHexPath } from './utils/hexUtils.js';

// 为 roundRect 做 polyfill
if (!(CanvasRenderingContext2D as any).roundRect) {
  (CanvasRenderingContext2D as any).roundRect = function(x: number, y: number, w: number, h: number, r: number) {
    const radius = Math.min(r, w / 2, h / 2);
    this.beginPath();
    this.moveTo(x + radius, y);
    this.arcTo(x + w, y, x + w, y + h, radius);
    this.arcTo(x + w, y + h, x, y + h, radius);
    this.arcTo(x, y + h, x, y, radius);
    this.arcTo(x, y, x + w, y, radius);
    this.closePath();
    return this;
  };
}

type ModalType = 'none' | 'won' | 'lost' | 'levelSelect' | 'confirm' | 'loadFailed';

// 确认弹窗的上下文（用于重开确认后再重试）
interface ConfirmContext {
  action: 'restart' | 'retryLoad';
  data?: any;
}

export class GameApp {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private renderer: GameRenderer | null = null;
  private ui: UI;
  private loader: ImageLoader = new ImageLoader();
  private pieceRenderer: PieceRenderer = new PieceRenderer();
  private game: PuzzleGame | null = null;
  private layout: GameLayout | null = null;
  private musicOn: boolean = true;
  private musicAudio: HTMLAudioElement | null = null;
  private modalType: ModalType = 'none';
  private confirmContext: ConfirmContext | null = null;
  private levelIndex: { levelId: string; levelNumber: number }[] = [];
  private configCache: Map<number, LevelConfig> = new Map();
  private currentLevelNumber: number = 1;
  private pieceImages: Map<string, HTMLCanvasElement> = new Map();
  private lastTime: number = 0;
  private animFrameId: number = 0;

  // 当前关卡的完整图片缓存（胜利弹窗用）
  private currentFullImage: HTMLImageElement | null = null;

  // 拖拽状态
  private dragging: boolean = false;
  private draggedPieceId: string | null = null;
  private dragOffsetX: number = 0;  // 手指到 piece canvas 中心的固定偏移（开始拖拽时计算）
  private dragOffsetY: number = 0;
  private dragPos: { x: number; y: number } = { x: 0, y: 0 };

  // 移动端触摸状态
  private isMobile: boolean = false;

  // 选关弹窗的关卡缩略图（懒加载）
  private levelThumbnails: Map<number, HTMLImageElement> = new Map();

  constructor() {
    this.canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;
    this.ui = new UI(this.canvas);

    // 初始化音乐
    this.musicAudio = new Audio('music/music.mp3');
    this.musicAudio.loop = true;
    this.musicAudio.volume = 0.3;

    // 加载UI图片
    this.loader.load('UI/clock.png').catch(() => {});
    this.loader.load('UI/lose.png').catch(() => {});
    this.loader.load('UI/lose_pic.png').catch(() => {});
    this.loader.load('UI/lose_text.png').catch(() => {});
    this.loader.load('UI/next.png').catch(() => {});

    // 绑定事件
    this.bindEvents();
    this.resize();
  }

  private async init(): Promise<void> {
    // 只加载关卡索引（轻量），不加载所有配置
    await this.loadLevelIndex();

    // 加载UI图片到UI
    this.ui.setImages({
      clock: this.loader.get('UI/clock.png'),
      next: this.loader.get('UI/next.png'),
      lose: this.loader.get('UI/lose.png'),
      lose_pic: this.loader.get('UI/lose_pic.png'),
      lose_text: this.loader.get('UI/lose_text.png'),
    });

    // 加载玩家当前可挑战的最大关卡：max(已通关) + 1
    const completed = getCompletedLevels();
    const maxCompleted = completed.length > 0 ? Math.max(...completed) : 0;
    const startLevel = Math.min(maxCompleted + 1, this.levelIndex.length);
    await this.loadLevel(startLevel);

    // 开始游戏循环
    this.startGameLoop();
  }

  private async loadLevelIndex(): Promise<void> {
    try {
      const response = await fetch('level_list/index.json');
      if (response.ok) {
        const data = await response.json();
        const levelIds: string[] = data.levelIds || [];
        // levelIds 按顺序排列，levelXXXX 的数字即为 levelNumber
        this.levelIndex = levelIds.map((id, i) => ({
          levelId: id,
          levelNumber: i + 1,
        }));
        console.log(`关卡索引加载完成: ${this.levelIndex.length} 关`);
      }
    } catch (e) {
      console.warn('加载关卡索引失败', e);
    }
  }

  // 按需加载关卡配置（带缓存）
  private async getOrLoadConfig(levelNumber: number): Promise<LevelConfig | null> {
    if (this.configCache.has(levelNumber)) return this.configCache.get(levelNumber)!;
    const entry = this.levelIndex.find(e => e.levelNumber === levelNumber);
    if (!entry) return null;
    try {
      const cfg = await loadLevelConfig(entry.levelId);
      this.configCache.set(levelNumber, cfg);
      return cfg;
    } catch (e) {
      console.warn(`加载关卡配置失败: ${entry.levelId}`, e);
      return null;
    }
  }

  // 预加载下一关配置 + 图片（非阻塞）
  private preloadNext(levelNumber: number): void {
    const nextNum = levelNumber + 1;
    if (this.configCache.has(nextNum)) return;
    const entry = this.levelIndex.find(e => e.levelNumber === nextNum);
    if (!entry) return;
    loadLevelConfig(entry.levelId).then(cfg => {
      this.configCache.set(nextNum, cfg);
      if (cfg) {
        const fileName = cfg.imageAssetPath.split('/').pop()!;
        this.loader.load(`level_pic/${fileName}`).catch(() => {});
      }
    }).catch(() => {});
  }

  private async loadLevel(levelNumber: number): Promise<void> {
    // 按需加载关卡配置
    const config = await this.getOrLoadConfig(levelNumber);
    if (!config) {
      console.error(`找不到关卡 ${levelNumber} 的配置`);
      return;
    }

    this.currentLevelNumber = levelNumber;

    // 创建游戏实例
    this.game = new PuzzleGame(config);

    // 加载图片
    let fullImage: HTMLImageElement | null = null;
    try {
      fullImage = await loadLevelImage(config.imageAssetPath, this.loader);
    } catch (e) {
      console.error('加载关卡图片失败', e);
    }

    // 更新布局
    this.updateLayout();

    // 生成所有碎片图片
    this.pieceImages.clear();
    if (fullImage) {
      for (const piece of config.pieces) {
        try {
          const canvas = this.pieceRenderer.generatePieceImage(piece, fullImage, config.grid);
          this.pieceImages.set(piece.id, canvas);
        } catch (e) {
          console.warn(`生成碎片图片失败: ${piece.id}`, e);
        }
      }
    }

    // 创建渲染器
    this.renderer = new GameRenderer(this.canvas, config, fullImage, this.layout!);
    this.renderer.setPieceImages(this.pieceImages);

    // 设置 UI 的完整图片（胜利弹窗用）
    this.currentFullImage = fullImage;
    this.ui.setFullImage(fullImage);

    // 预加载下一关（配置 + 图片，非阻塞）
    this.preloadNext(levelNumber);

    // 触发初始解锁动画
    for (const [key, cellInfo] of this.game.cells) {
      const [colStr, rowStr] = key.split(',');
      if (cellInfo.state === 'opened') {
        this.renderer.triggerUnlock(parseInt(colStr), parseInt(rowStr));
      }
    }
  }

  private updateLayout(): void {
    const totalWidth = Math.max(1, this.canvas.width);
    const totalHeight = Math.max(1, this.canvas.height);
    this.isMobile = totalWidth < 768;

    let gameArea: { x: number; y: number; width: number; height: number };
    let phoneFrame: { x: number; y: number; width: number; height: number; radius: number } | null = null;
    let panelX: number = 0;

    if (this.isMobile) {
      // 移动端：全屏
      gameArea = { x: 0, y: 0, width: totalWidth, height: totalHeight };
    } else {
      // ====== 参考网站精确尺寸 ======
      // 左侧面板：固定 360px 宽
      const panelW = 360;
      // 手机外框：高度为页面的 80%，宽度按比例
      const phoneHeight = totalHeight * 0.8;
      const phoneWidth = phoneHeight * 0.461;
      // gap 40px
      const gap = 40;

      // 整体水平居中：面板 + gap + 手机框
      const totalLayoutW = panelW + gap + phoneWidth;
      const layoutStartX = (totalWidth - totalLayoutW) / 2;
      panelX = layoutStartX;
      const phoneFrameX = layoutStartX + panelW + gap;
      const phoneFrameY = (totalHeight - phoneHeight) / 2;

      // 手机外框
      const frameBorder = 14;
      phoneFrame = {
        x: phoneFrameX,
        y: phoneFrameY,
        width: phoneWidth,
        height: phoneHeight,
        radius: 44,
      };

      // 游戏区域 = 手机屏幕内缩 + 32px 屏幕圆角
      gameArea = {
        x: phoneFrameX + frameBorder,
        y: phoneFrameY + frameBorder,
        width: phoneWidth - frameBorder * 2,
        height: phoneHeight - frameBorder * 2,
      };
    }

    // 顶部栏：最低游戏高度的 1/12
    const topBarHeight = Math.max(72, Math.floor(gameArea.height / 12));
    const pieceAreaHeight = gameArea.height * 0.18;
    // 道具栏：最低游戏高度的 1/15
    const itemAreaHeight = Math.max(50, Math.floor(gameArea.height / 15));
    const puzzleAreaHeight = gameArea.height - topBarHeight - pieceAreaHeight - itemAreaHeight;

    // 拼图区域内图片的缩放
    const config = this.game?.config;
    let puzzleScale = 1;
    let puzzleOffset = { x: 0, y: 0 };

    if (config) {
      const imgW = config.imageSize?.width ?? 750;
      const imgH = config.imageSize?.height ?? 980;
      const availablePuzzleW = gameArea.width - 16;
      const availablePuzzleH = puzzleAreaHeight - 16;

      puzzleScale = Math.min(availablePuzzleW / imgW, availablePuzzleH / imgH);
      const scaledW = imgW * puzzleScale;
      const scaledH = imgH * puzzleScale;
      // puzzleOffset 只表示 puzzleArea 内部的偏移（drawPuzzleArea 会再加 puzzleArea.y）
      puzzleOffset = {
        x: (gameArea.width - scaledW) / 2,
        y: (puzzleAreaHeight - scaledH) / 2,
      };
    }

    this.layout = {
      gameArea,
      phoneFrame,
      leftPanelX: panelX,
      puzzleArea: {
        x: gameArea.x,
        y: gameArea.y + topBarHeight,
        width: gameArea.width,
        height: puzzleAreaHeight,
      },
      topBarHeight,
      pieceAreaHeight,
      itemAreaHeight,
      puzzleScale,
      puzzleOffset,
    };

    this.ui.setLayout(this.layout);
    if (this.renderer) {
      this.renderer.setLayout(this.layout);
    }
  }

  private resize(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.updateLayout();
  }

  private bindEvents(): void {
    // 窗口大小变化
    window.addEventListener('resize', () => this.resize());

    // 鼠标事件（PC）
    this.canvas.addEventListener('mousedown', (e) => this.onPointerDown(e.clientX, e.clientY));
    this.canvas.addEventListener('mousemove', (e) => this.onPointerMove(e.clientX, e.clientY));
    this.canvas.addEventListener('mouseup', (e) => this.onPointerUp(e.clientX, e.clientY));

    // 鼠标滚轮（PC 选关弹窗滚动）
    this.canvas.addEventListener('wheel', (e) => {
      if (this.modalType === 'levelSelect') {
        e.preventDefault();
        this.ui.levelSelectScrollOffset += e.deltaY;
      }
    }, { passive: false });

    // 触摸事件（移动端）
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.touches[0];
      this.onTouchStartForScroll(t.clientX, t.clientY);
      this.onPointerDown(t.clientX, t.clientY);
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const t = e.touches[0];
      this.onTouchMoveForScroll(t.clientX, t.clientY);
      this.onPointerMove(t.clientX, t.clientY);
    }, { passive: false });

    this.canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      this.onTouchEndForScroll();
      this.onPointerUp(t.clientX, t.clientY);
    }, { passive: false });
  }

  // 选关弹窗滚动状态（触摸 + 桌面鼠标拖动）
  private touchScrollStartY: number | null = null;
  private touchScrollLastY: number | null = null;
  private levelSelectDragScrolling: boolean = false;
  private levelSelectDragLastY: number = 0;
  private levelSelectDragMoved: boolean = false;

  private onTouchStartForScroll(x: number, y: number): void {
    if (this.modalType === 'levelSelect') {
      this.touchScrollStartY = y;
      this.touchScrollLastY = y;
    } else {
      this.touchScrollStartY = null;
    }
  }

  private onTouchMoveForScroll(x: number, y: number): void {
    if (this.modalType === 'levelSelect' && this.touchScrollLastY !== null) {
      const dy = y - this.touchScrollLastY;
      this.ui.levelSelectScrollOffset -= dy; // 手指上滑→内容向上→scrollOffset 增加
      this.touchScrollLastY = y;
    }
  }

  private onTouchEndForScroll(): void {
    this.touchScrollStartY = null;
    this.touchScrollLastY = null;
  }

  private getCanvasPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private onPointerDown(clientX: number, clientY: number): void {
    if (!this.game) return;
    const pt = this.getCanvasPoint(clientX, clientY);

    // 弹窗优先处理
    if (this.modalType !== 'none') {
      if (this.modalType === 'won' || this.modalType === 'lost') {
        const result = this.ui.hitTestModal(pt.x, pt.y, this.modalType);
        if (result === 'nextLevel') {
          this.handleNextLevel();
        } else if (result === 'restart') {
          this.handleRestart();
        }
        this.modalType = 'none';
        return;
      } else if (this.modalType === 'confirm' || this.modalType === 'loadFailed') {
        const result = this.ui.hitTestModal(pt.x, pt.y, this.modalType);
        if (result === 'confirm') {
          if (this.confirmContext?.action === 'restart') {
            this.handleRestart();
          } else if (this.confirmContext?.action === 'retryLoad') {
            // 重试加载当前关卡
            const num = this.confirmContext.data || this.currentLevelNumber;
            this.loadLevel(num);
          }
        }
        this.modalType = 'none';
        this.confirmContext = null;
        return;
      } else if (this.modalType === 'levelSelect') {
        // 启动桌面鼠标拖动滚动追踪
        this.levelSelectDragScrolling = true;
        this.levelSelectDragLastY = pt.y;
        this.levelSelectDragMoved = false;
        return;
      }
    }

    // 检查UI按钮
    const uiHit = this.ui.hitTestUI(pt.x, pt.y);
    if (uiHit === 'music') {
      this.toggleMusic();
      return;
    } else if (uiHit === 'restart') {
      // 重开：弹确认框
      this.modalType = 'confirm';
      this.confirmContext = { action: 'restart' };
      return;
    } else if (uiHit === 'levelSelect') {
      // 选关：打开选关弹窗
      this.ui.levelSelectScrollOffset = 0;
      this.modalType = 'levelSelect';
      return;
    } else if (uiHit === 'magnet') {
      if (this.game.items.magnet > 0) {
        this.game.useMagnet();
      }
      return;
    } else if (uiHit === 'hint') {
      if (this.game.items.hint > 0) {
        const hintData = this.game.useHint();
        if (hintData) {
          const mapped = hintData.map(h => ({ pieceId: h.piece.id, cells: h.correctCells }));
          this.ui.setHintData(mapped);
        }
      }
      return;
    }

    // 检查是否点击了碎片区的碎片（开始拖拽）
    if (this.isPointInPieceArea(pt.x, pt.y)) {
      const pieceId = this.getPieceAtPosition(pt.x, pt.y);
      if (pieceId) {
        this.dragging = true;
        this.draggedPieceId = pieceId;
        this.dragPos = pt;

        // 计算 piece canvas 在 slot 内的中心位置，保存手指到中心的偏移
        const pieceImg = this.renderer?.getPieceImage(pieceId);
        if (pieceImg && this.game) {
          const piece = this.game!.displayPieces.find(p => p.id === pieceId);
          if (piece) {
            const pieceArea = this.getPieceAreaRect();
            const slotWidth = pieceArea.width / 3;
            const i = this.game!.displayPieces.indexOf(piece);
            const slotX = pieceArea.x + i * slotWidth;
            const padding = 10;
            const scale = Math.min(
              (slotWidth - padding * 2) / pieceImg.width,
              (pieceArea.height - padding * 2) / pieceImg.height,
              1
            );
            const drawW = pieceImg.width * scale;
            const drawH = pieceImg.height * scale;
            const pieceCx = slotX + slotWidth / 2;
            const pieceCy = pieceArea.y + pieceArea.height / 2;
            // 手指相对 piece canvas 中心的偏移（注意 drawImage 是左上角，中心 = drawX + drawW/2）
            const drawX = slotX + (slotWidth - drawW) / 2;
            const drawY = pieceArea.y + (pieceArea.height - drawH) / 2;
            const centerX = drawX + drawW / 2;
            const centerY = drawY + drawH / 2;
            this.dragOffsetX = pt.x - centerX;
            this.dragOffsetY = pt.y - centerY;
          }
        }
      }
    }
  }

  private onPointerMove(clientX: number, clientY: number): void {
    const pt = this.getCanvasPoint(clientX, clientY);
    this.dragPos = pt;

    // 桌面端选关弹窗拖动滚动
    if (this.levelSelectDragScrolling) {
      const dy = pt.y - this.levelSelectDragLastY;
      if (Math.abs(dy) > 2) this.levelSelectDragMoved = true;
      this.ui.levelSelectScrollOffset -= dy; // 上滑→offset增加
      this.levelSelectDragLastY = pt.y;
    }
  }

  private onPointerUp(clientX: number, clientY: number): void {
    // 桌面端选关弹窗：松手时决定是否触发点击
    if (this.levelSelectDragScrolling) {
      const pt = this.getCanvasPoint(clientX, clientY);
      const wasDragged = this.levelSelectDragMoved;
      this.levelSelectDragScrolling = false;
      this.levelSelectDragMoved = false;

      if (!wasDragged) {
        // 没拖动 → 当作点击处理
        const configs = this.levelIndex.map(e => ({ levelNumber: e.levelNumber }));
        const result = this.ui.hitTestModal(pt.x, pt.y, 'levelSelect', undefined, configs);
        if (result === 'close') {
          this.modalType = 'none';
        } else if (result && result.startsWith('level_')) {
          const num = parseInt(result.replace('level_', ''));
          this.modalType = 'none';
          if (num === 1 || getCompletedLevels().includes(num - 1)) {
            this.loadLevel(num);
          }
        }
      }
      return;
    }

    if (!this.dragging || !this.draggedPieceId || !this.game) {
      this.dragging = false;
      this.draggedPieceId = null;
      return;
    }

    const pt = this.getCanvasPoint(clientX, clientY);
    const renderer = this.renderer;

    if (!renderer || !this.layout) {
      this.dragging = false;
      this.draggedPieceId = null;
      return;
    }

    // 检查松手位置是否在拼图区域
    const { puzzleArea } = this.layout;
    if (pt.x >= puzzleArea.x && pt.x <= puzzleArea.x + puzzleArea.width &&
        pt.y >= puzzleArea.y && pt.y <= puzzleArea.y + puzzleArea.height) {

      // 找到对应的cell
      const cell = renderer.screenToCell(pt.x, pt.y);
      if (cell) {
        const piece = this.game.displayPieces.find(p => p.id === this.draggedPieceId);
        if (piece) {
          const result = this.game.tryPlacePiece(piece, cell);
          if (result.success) {
            // 触发解锁动画
            const placedCells = this.game.placedPieces[this.game.placedPieces.length - 1].cells;
            const neighbors: { col: number; row: number }[] = [];
            for (const pc of placedCells) {
              const n = [
                { col: pc.col, row: pc.row - 1 },
                { col: pc.col, row: pc.row + 1 },
                { col: pc.col - 1, row: pc.row },
                { col: pc.col + 1, row: pc.row },
                { col: pc.col - 1, row: pc.row - 1 },
                { col: pc.col + 1, row: pc.row - 1 },
              ];
              neighbors.push(...n);
            }
            // 触发所有新解锁的cells动画
            if (renderer) {
              for (const [key, info] of this.game.cells) {
                const [colStr, rowStr] = key.split(',');
                if (info.state === 'opened') {
                  renderer.triggerUnlock(parseInt(colStr), parseInt(rowStr));
                }
              }
            }
          }
        }
      }
    }

    // 重置拖拽状态
    this.dragging = false;
    this.draggedPieceId = null;

    // 检查胜利/失败
    if (this.game.phase === 'won') {
      this.onWin();
    } else if (this.game.phase === 'lost') {
      this.onLose();
    }
  }

  private getPieceAreaRect(): { x: number; y: number; width: number; height: number } {
    if (!this.layout) return { x: 0, y: 0, width: 0, height: 0 };
    const { gameArea, pieceAreaHeight, itemAreaHeight } = this.layout;
    const pieceAreaY = gameArea.y + gameArea.height - itemAreaHeight - pieceAreaHeight;
    return { x: gameArea.x, y: pieceAreaY, width: gameArea.width, height: pieceAreaHeight };
  }

  private isPointInPieceArea(x: number, y: number): boolean {
    const r = this.getPieceAreaRect();
    return x >= r.x && x <= r.x + r.width && y >= r.y && y < r.y + r.height;
  }

  private getPieceAtPosition(x: number, y: number): string | null {
    if (!this.game || !this.layout) return null;
    const { gameArea, pieceAreaHeight, itemAreaHeight } = this.layout;
    const pieceAreaY = gameArea.y + gameArea.height - itemAreaHeight - pieceAreaHeight;
    const pieceAreaWidth = gameArea.width;
    const slotWidth = pieceAreaWidth / 3;
    const padding = 10;

    for (let i = 0; i < this.game.displayPieces.length; i++) {
      const piece = this.game.displayPieces[i];
      if (!piece) continue;
      const pieceImg = this.pieceImages.get(piece.id);
      if (!pieceImg) continue;

      const slotX = gameArea.x + i * slotWidth;
      const scale = Math.min(
        (slotWidth - padding * 2) / pieceImg.width,
        (pieceAreaHeight - padding * 2) / pieceImg.height,
        1
      );
      const drawW = pieceImg.width * scale;
      const drawH = pieceImg.height * scale;
      const drawX = slotX + (slotWidth - drawW) / 2;
      const drawY = pieceAreaY + (pieceAreaHeight - drawH) / 2;

      if (x >= drawX && x <= drawX + drawW && y >= drawY && y <= drawY + drawH) {
        return piece.id;
      }
    }
    return null;
  }

  private toggleMusic(): void {
    this.musicOn = !this.musicOn;
    if (this.musicAudio) {
      if (this.musicOn) {
        this.musicAudio.play().catch(() => {});
      } else {
        this.musicAudio.pause();
      }
    }
  }

  private onWin(): void {
    // 保存通关记录
    if (this.game) {
      addCompletedLevel(this.game.config.levelNumber);
    }
    this.modalType = 'won';
  }

  private onLose(): void {
    this.modalType = 'lost';
  }

  private handleNextLevel(): void {
    if (!this.game) return;
    const nextNumber = this.game.config.levelNumber + 1;
    if (this.levelIndex.some(e => e.levelNumber === nextNumber)) {
      this.loadLevel(nextNumber);
    } else {
      // 最后一关，打开选关弹窗
      this.ui.levelSelectScrollOffset = 0;
      this.modalType = 'levelSelect';
    }
  }

  private handleRestart(): void {
    if (this.game) {
      this.loadLevel(this.game.config.levelNumber);
    }
  }

  // 用 arcTo 构建圆角矩形并 clip（兼容所有浏览器）
  private arcRoundRectClip(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    if (w <= 0 || h <= 0) return;
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.arcTo(x + w, y, x + w, y + rr, rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
    ctx.lineTo(x + rr, y + h);
    ctx.arcTo(x, y + h, x, y + h - rr, rr);
    ctx.lineTo(x, y + rr);
    ctx.arcTo(x, y, x + rr, y, rr);
    ctx.closePath();
    ctx.clip();
  }

  private startGameLoop(): void {
    this.lastTime = performance.now();
    const loop = (now: number) => {
      const delta = now - this.lastTime;
      this.lastTime = now;
      this.update(delta);
      this.render();
      this.animFrameId = requestAnimationFrame(loop);
    };
    this.animFrameId = requestAnimationFrame(loop);
  }

  private update(deltaMs: number): void {
    if (!this.game) return;

    // 更新游戏时间
    if (this.game.startTime !== null) {
      this.game.updateTime(deltaMs);
    }

    // 检查游戏状态变化
    if (this.game.phase === 'won' && this.modalType !== 'won') {
      this.onWin();
    } else if (this.game.phase === 'lost' && this.modalType !== 'lost') {
      this.onLose();
    }

    // 更新动画
    if (this.renderer) {
      this.renderer.updateAnims(deltaMs);
    }
  }

  private render(): void {
    if (!this.game || !this.layout || !this.renderer) return;

    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // PC端：左侧毛玻璃面板
    if (!this.isMobile) {
      this.drawIntro();
    }

    // 绘制手机外框（桌面端）或游戏区域背景（移动端）
    if (!this.isMobile && this.layout.phoneFrame) {
      this.drawPhoneFrame();
    } else {
      const { gameArea } = this.layout;
      // 和桌面端手机屏幕同色：米黄色
      ctx.fillStyle = 'rgb(245, 239, 224)';
      ctx.fillRect(gameArea.x, gameArea.y, gameArea.width, gameArea.height);
    }

    // 裁剪到手机屏幕圆角内（桌面端 32px），防止内容溢出显示黑角
    ctx.save();
    if (!this.isMobile && this.layout.phoneFrame) {
      const { phoneFrame } = this.layout;
      const frameBorder = 14;
      const sx = phoneFrame.x + frameBorder;
      const sy = phoneFrame.y + frameBorder;
      const sw = phoneFrame.width - frameBorder * 2;
      const sh = phoneFrame.height - frameBorder * 2;
      this.arcRoundRectClip(ctx, sx, sy, sw, sh, 32);
    }

    // 渲染拼图区域
    this.renderer.drawPuzzleArea(this.game);

    // 渲染碎片区
    this.renderer.drawPieceArea(this.game, this.dragging ? this.draggedPieceId : null, this.dragPos.x, this.dragPos.y);

    // 渲染拖拽中的碎片 —— 用固定偏移跟随手指（不会漂移跳动）
    if (this.dragging && this.draggedPieceId) {
      // 把 piece canvas 中心放在 (dragPos - dragOffset)
      const targetX = this.dragPos.x - this.dragOffsetX;
      const targetY = this.dragPos.y - this.dragOffsetY;
      this.renderer.drawDraggingPiece(this.game, this.draggedPieceId, targetX, targetY);
    }

    // 渲染UI
    this.ui.drawTopBar(this.game, this.musicOn);
    this.ui.drawItemArea(this.game);

    // 渲染查看道具虚影
    const hintData = this.ui.getHintData();
    if (hintData) {
      this.renderer.drawHintGhosts(hintData);
    }

    // 渲染弹窗（必须在 ctx.restore() 之前，才能被圆角 clip 裁剪）
    if (this.modalType === 'won') {
      this.ui.drawWonModal(this.game);
    } else if (this.modalType === 'lost') {
      this.ui.drawLostModal();
    } else if (this.modalType === 'confirm') {
      this.ui.drawConfirmModal('重新开始', '是否重新开始本轮游戏？', '确定', '取消');
    } else if (this.modalType === 'loadFailed') {
      this.ui.drawLoadFailedModal();
    } else if (this.modalType === 'levelSelect') {
      const configsWithThumb = this.prepareLevelThumbnails();
      this.ui.drawLevelSelectModal(configsWithThumb, getCompletedLevels());
    }

    ctx.restore();
  }

  // 绘制精致的手机外框（参考网站精确样式）
  private drawPhoneFrame(): void {
    if (!this.layout?.phoneFrame) return;
    const ctx = this.ctx;
    const { phoneFrame } = this.layout;
    const { x, y, width, height, radius } = phoneFrame;
    const frameBorder = 14;

    // 1. 外阴影：rgba(0,0,0,0.5) 0px 30px 60px
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 60;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 30;

    // 手机外框主体：linear-gradient(145deg, rgb(42,42,42), rgb(21,21,21))
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    const metalGrad = ctx.createLinearGradient(x, y, x + width, y + height);
    metalGrad.addColorStop(0, 'rgb(42, 42, 42)');
    metalGrad.addColorStop(1, 'rgb(21, 21, 21)');
    ctx.fillStyle = metalGrad;
    ctx.fill();
    ctx.restore();

    // 2. 内高光/内阴影：rgba(255,255,255,0.12)
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x + 2, y + 2, width - 4, height - 4, radius - 2);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // 3. 屏幕内凹（米黄色画布背景）
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x + frameBorder, y + frameBorder, width - frameBorder * 2, height - frameBorder * 2, 32);
    ctx.fillStyle = 'rgb(245, 239, 224)'; // 米黄色
    ctx.fill();
    ctx.restore();
  }

  // 准备选关缩略图（非阻塞）
  private prepareLevelThumbnails(): { levelNumber: number; thumbnail?: HTMLImageElement }[] {
    const result: { levelNumber: number; thumbnail?: HTMLImageElement }[] = [];
    for (const entry of this.levelIndex) {
      const cached = this.levelThumbnails.get(entry.levelNumber);
      if (cached) {
        result.push({ levelNumber: entry.levelNumber, thumbnail: cached });
      } else {
        // 触发预加载（需要 config 来拿 imageAssetPath，但 config 可能还没加载）
        // 先尝试从文件名推断：文件名就是 levelXXXX.png？不，是 GXXXX.png
        // 所以需要 config 来拿到 imageAssetPath。懒加载 config 然后缓存缩略图。
        result.push({ levelNumber: entry.levelNumber });
        // 非阻塞：加载 config → 提取图片路径 → 加载图片 → 缓存缩略图
        this.getOrLoadConfig(entry.levelNumber).then(cfg => {
          if (!cfg) return;
          const fileName = cfg.imageAssetPath.split('/').pop()!;
          return this.loader.load(`level_pic/${fileName}`);
        }).then(img => {
          if (img) this.levelThumbnails.set(entry.levelNumber, img);
        }).catch(() => {});
      }
    }
    return result;
  }

  private drawIntro(): void {
    const ctx = this.ctx;
    if (this.isMobile) return;

    // 面板独立高度，不跟随手机框
    const panelW = 360;
    const panelH = 520;
    const panelX = this.layout?.leftPanelX ?? 40;
    // 面板整体垂直居中（相对手机框区域）
    const phoneFrameY = this.layout?.phoneFrame?.y ?? 0;
    const phoneFrameH = this.layout?.phoneFrame?.height ?? this.canvas.height;
    const panelY = phoneFrameY + (phoneFrameH - panelH) / 2;

    // 1. 面板主体 + 边框
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelW, panelH, 16);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const padX = 24;
    let ty = panelY + 48;
    const centerX = panelX + panelW / 2;

    // 2. 标题
    ctx.fillStyle = 'rgb(245, 233, 201)';
    ctx.font = 'bold 32px "KaiTi", "STKaiti", "Microsoft YaHei", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    this.drawTextWithSpacing(ctx, '六边形拼图', centerX, ty, 4);

    // 3. 副标题
    ty += 38;
    ctx.fillStyle = 'rgb(168, 152, 128)';
    ctx.font = '14px "KaiTi", "STKaiti", "Microsoft YaHei", serif';
    ctx.fillText('— 挑战你的空间想象力 —', centerX, ty);

    // 金色竖条装饰
    ctx.save();
    const decorLineX = panelX + padX + 2;
    ctx.strokeStyle = 'rgba(212, 168, 83, 0.5)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(decorLineX, ty + 8);
    ctx.lineTo(decorLineX, ty + 40);
    ctx.stroke();
    ctx.restore();

    // 4. 玩法说明区块
    ty += 48;
    const blockX = panelX + padX;
    const blockW = panelW - padX * 2;
    let blockStartY = ty;

    ctx.beginPath();
    ctx.roundRect(blockX, ty, blockW, 160, 10);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.fill();

    ty = blockStartY + 18;
    ctx.fillStyle = 'rgba(245, 233, 201, 0.9)';
    ctx.font = 'bold 17px "KaiTi", "Microsoft YaHei", serif';
    ctx.textAlign = 'left';
    ctx.fillText('🎮 玩法说明', blockX + 14, ty);

    ty += 26;
    ctx.fillStyle = 'rgba(168, 152, 128, 0.9)';
    ctx.font = '16px "KaiTi", "Microsoft YaHei", serif';
    const rules = [
      '• 拖拽碎片到拼图区域',
      '• 形状 + 位置双重匹配',
      '• 放错不惩罚时间',
      '• 连锁解锁周边格子',
      '• 初始 3 块从边缘开启',
    ];
    for (const line of rules) {
      ctx.fillText(line, blockX + 14, ty);
      ty += 22;
    }

    // 5. 道具说明区块
    ty = blockStartY + 160 + 14;
    blockStartY = ty;
    ctx.beginPath();
    ctx.roundRect(blockX, ty, blockW, 86, 10);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.fill();

    ty += 18;
    ctx.fillStyle = 'rgba(245, 233, 201, 0.9)';
    ctx.font = 'bold 17px "KaiTi", "Microsoft YaHei", serif';
    ctx.fillText('🧰 道具', blockX + 14, ty);

    ty += 26;
    ctx.fillStyle = 'rgba(168, 152, 128, 0.9)';
    ctx.font = '16px "KaiTi", "Microsoft YaHei", serif';
    ctx.fillText('🧲 磁力  · 自动吸附第一块', blockX + 14, ty);
    ty += 22;
    ctx.fillText('👁️ 查看  · 5秒虚影提示', blockX + 14, ty);

    // 6. 底部提示
    ty = panelY + panelH - 28;
    ctx.fillStyle = 'rgb(122, 112, 96)';
    ctx.font = '12px "KaiTi", "Microsoft YaHei", serif';
    ctx.textAlign = 'center';
    ctx.fillText('建议在手机上获得最佳体验', centerX, ty);
  }

  // 带字间距的文字绘制
  private drawTextWithSpacing(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, spacing: number): void {
    let totalWidth = 0;
    const charWidths: number[] = [];
    for (const ch of text) {
      const w = ctx.measureText(ch).width;
      charWidths.push(w);
      totalWidth += w;
    }
    totalWidth += spacing * (text.length - 1);
    let currentX = x - totalWidth / 2;
    for (let i = 0; i < text.length; i++) {
      ctx.fillText(text[i], currentX + charWidths[i] / 2, y);
      currentX += charWidths[i] + spacing;
    }
  }

  async start(): Promise<void> {
    try {
      await this.init();
      console.log('游戏初始化完成');
    } catch (e) {
      console.error('游戏初始化失败', e);
    }
  }
}
